/**
 * ScrollTrigger: pin product gallery beside details (desktop).
 * ScrollToPlugin: scroll #personalized-preview (or gallery column) after variant changes.
 * After Section Rendering API swaps invoke window.__reinitPdpRedesignStickyPin().
 */
(function () {
  /** @type {ReturnType<typeof gsap.matchMedia> | null} */
  var pdpMm = null;

  function debounce(fn, ms) {
    var t;
    return function () {
      clearTimeout(t);
      var a = arguments;
      var ctx = this;
      t = setTimeout(function () {
        fn.apply(ctx, a);
      }, ms);
    };
  }

  function installScrollPreviewHelper(gsapApi, ScrollTriggerConstructor, ScrollToPluginConstructor) {
    window.__pdpRedesignScrollPreviewAfterVariant = function () {
      if (!window.matchMedia('(min-width: 768px)').matches) {
        return;
      }

      var root = document.querySelector('[data-product-info-redesign-root]');
      if (!root) {
        return;
      }

      var preview = document.getElementById('personalized-preview');
      var gallery = root.querySelector('.js-gallery-redesign');

      var target = null;
      if (preview) {
        var pr = preview.getBoundingClientRect();
        if (pr.height > 0 && pr.width > 0) {
          target = preview;
        }
      }
      if (!target && gallery) {
        target = gallery;
      }
      if (!target) {
        return;
      }

      if (typeof ScrollTriggerConstructor !== 'undefined') {
        ScrollTriggerConstructor.refresh(true);
      }

      var reduceMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
      var offsetY = 100;

      if (typeof ScrollToPluginConstructor !== 'undefined' && gsapApi) {
        gsapApi.to(window, {
          scrollTo: { y: target, offsetY: offsetY, autoKill: true },
          duration: reduceMotion ? 0 : 0.55,
          ease: reduceMotion ? 'none' : 'power2.out',
        });
      } else {
        var top =
          window.scrollY + target.getBoundingClientRect().top - offsetY;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: reduceMotion ? 'instant' : 'smooth',
        });
      }
    };
  }

  function revertMatchMedia() {
    try {
      if (pdpMm && typeof pdpMm.revert === 'function') {
        pdpMm.revert();
      }
    } catch (e) {}
    if (typeof gsap !== 'undefined' && gsap.matchMedia) {
      pdpMm = gsap.matchMedia();
    } else {
      pdpMm = null;
    }
  }

  function init() {
    if (typeof gsap === 'undefined') {
      return;
    }

    revertMatchMedia();

    var hasST = typeof ScrollTrigger !== 'undefined';
    var hasScrollTo = typeof ScrollToPlugin !== 'undefined';

    if (hasST && hasScrollTo) {
      gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);
    } else if (hasST) {
      gsap.registerPlugin(ScrollTrigger);
    } else if (hasScrollTo) {
      gsap.registerPlugin(ScrollToPlugin);
    }

    installScrollPreviewHelper(
      gsap,
      hasST ? ScrollTrigger : undefined,
      hasScrollTo ? ScrollToPlugin : undefined
    );

    if (!hasST || !pdpMm) {
      return;
    }

    var reducedMotionPin = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    if (reducedMotionPin) {
      return;
    }

    pdpMm.add('(min-width: 768px)', function () {
      var root = document.querySelector('[data-product-info-redesign-root]');
      if (!root) {
        return function () {};
      }
      var gallery = root.querySelector('.js-gallery-redesign');
      var details = root.querySelector('.js-product-details-container');
      if (!gallery || !details) {
        return function () {};
      }

      ScrollTrigger.getAll().forEach(function (st) {
        try {
          if (gallery === st.trigger || st.pin === gallery || (st.vars && st.vars.trigger === gallery)) {
            st.kill(false);
          }
        } catch (e) {}
      });

      var st = ScrollTrigger.create({
        trigger: gallery,
        start: 'top top+=32',
        endTrigger: details,
        end: 'bottom bottom',
        pin: gallery,
        pinSpacing: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
      });

      root.setAttribute('data-scroll-pdp-pinned', '');
      root.classList.add('product-info-redesign--pin-active');
      gallery.classList.remove('md:sticky', 'md:top-8');
      window.dispatchEvent(new CustomEvent('scroll-pdp-pin-active'));

      var scheduleLayoutRefresh = debounce(function () {
        ScrollTrigger.refresh(true);
      }, 120);

      window.addEventListener('gallery-layout-changed', scheduleLayoutRefresh);
      window.addEventListener('pdp-product-switched', scheduleLayoutRefresh);

      var resizeObserver =
        typeof ResizeObserver !== 'undefined'
          ? new ResizeObserver(function () {
              scheduleLayoutRefresh();
            })
          : null;
      if (resizeObserver) {
        resizeObserver.observe(gallery);
      }

      function onGalleryResourceLoad() {
        scheduleLayoutRefresh();
      }
      gallery.addEventListener('load', onGalleryResourceLoad, true);

      return function cleanup() {
        st.kill(false);
        window.removeEventListener('gallery-layout-changed', scheduleLayoutRefresh);
        window.removeEventListener('pdp-product-switched', scheduleLayoutRefresh);
        gallery.removeEventListener('load', onGalleryResourceLoad, true);
        if (resizeObserver) {
          resizeObserver.disconnect();
        }
        root.removeAttribute('data-scroll-pdp-pinned');
        root.classList.remove('product-info-redesign--pin-active');
        gallery.classList.add('md:sticky', 'md:top-8');
        window.dispatchEvent(new CustomEvent('scroll-pdp-pin-inactive'));
      };
    });
  }

  window.__reinitPdpRedesignStickyPin = function () {
    if (typeof gsap === 'undefined') {
      return;
    }
    var run = function () {
      init();
      if (typeof ScrollTrigger !== 'undefined') {
        ScrollTrigger.refresh(true);
      }
    };
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(run);
    } else {
      run();
    }
  };

  function boot() {
    init();
    if (typeof ScrollTrigger !== 'undefined') {
      ScrollTrigger.refresh(true);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
